"use client";

import React, { useEffect, useState } from "react";
import { Tab, Row, Nav } from "react-bootstrap";
import { useTheme } from "@/app/theme-provider";
import { get, formatDate } from "@/app/helpers";
import { IMG_URL } from "@/app/config";
import { useRouter } from "next/navigation";

function getOrdersRoute(activeKey) {
  switch (String(activeKey)) {
    case "1":
      return "/tenders";
    case "2":
      return "/downloads/orders/government-orders";
    case "3":
      return "/downloads/orders/sos-and-proceedings";
    case "4":
      return "/downloads/act-and-rules";
    case "5":
      return "/downloads/orders/exam-details";
    default:
      return "/";
  }
}

export default function OrdersAndNotifications({
  initialTabsList = [],
  initialTabDetails = [],
  initialEportal = [],
  initialLanguage = "ml",
}) {
  const { theme, language } = useTheme();
  const router = useRouter();
  const [activeKey, setActiveKey] = useState(String(initialTabsList?.[0]?.id ?? "1"));
  const [tabsList, setTabsList] = useState(initialTabsList);
  const [tabDetails, setTabDetails] = useState(initialTabDetails);
  const [eportal, setEportal] = useState(initialEportal);
  const routeHref = getOrdersRoute(activeKey);

  useEffect(() => {
    // if (language === initialLanguage) {
    //   return undefined;
    // }

    let isMounted = true;

    async function loadLanguageData() {
      try {
        const [tabsRes, eportalRes] = await Promise.all([
          get("/website/homepage-tablist-all"),
          get("/website/homepage-e-portal"),
        ]);

        if (!isMounted) {
          return;
        }

        const nextTabs = tabsRes?.data ?? [];
        const nextActiveKey = String(nextTabs?.[0]?.id ?? "1");

        setTabsList(nextTabs);
        setEportal(eportalRes?.data ?? []);
        setActiveKey(nextActiveKey);

        const tabDetailsRes = await get(`/website/homepage-tablist/${nextActiveKey}`);
        if (isMounted) {
          setTabDetails(tabDetailsRes?.data ?? []);
        }
      } catch (_) {}
    }

    loadLanguageData();

    return () => {
      isMounted = false;
    };
  }, [initialLanguage, language]);

  useEffect(() => {
    const initialKey = String(initialTabsList?.[0]?.id ?? "1");
    if (!activeKey || (language === initialLanguage && activeKey === initialKey)) {
      return undefined;
    }

    let isMounted = true;

    get(`/website/homepage-tablist/${activeKey}`)
      .then((res) => {
        if (isMounted) {
          setTabDetails(res?.data ?? []);
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [activeKey, initialLanguage, initialTabsList, language]);

  return (
    <section className="cmpad mt-40 orders-notification head-p">
      <div className="row">
        <div className="col-lg-8 col-md-12 col-12 mt-3">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="head-h">
              {language == "en" ? "Orders & Notifications" : "ഓർഡറുകളും അറിയിപ്പുകളും"}
            </h2>
          </div>
          <p className="head-p">
            {language == "en"
              ? "Find out the latest orders and publications with ease."
              : "ഏറ്റവും പുതിയ ഓർഡറുകളും പ്രസിദ്ധീകരണങ്ങളും എളുപ്പത്തിൽ കണ്ടെത്തുക."}
          </p>
          <Tab.Container
            id="left-tabs-example"
            activeKey={activeKey}
            onSelect={(key) => setActiveKey(String(key))}
          >
            <Row>
              <div className="left-tab-menu">
                <Nav variant="pills" className="ltbmenu flex-column tab-btn me-0 me-sm-5 mt-4">
                  {tabsList?.map((item) => (
                    <Nav.Item className="my-tab" key={item.id}>
                      <Nav.Link
                        eventKey={String(item.id)}
                        className={`${activeKey == item.id ? "active-tab" : "tab-button"}`}
                      >
                        <img src="/icons/gavel.svg" alt={item.name} loading="lazy" decoding="async" />
                        {item.name}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </div>
              <div className="right-content">
                <Tab.Content className="mt-1 mt-sm-4">
                  <Tab.Pane eventKey={activeKey}>
                    <div className="s-tab">
                      {tabDetails?.map((item, index) => (
                        <div
                          key={item?.id ?? index}
                          className="s-tab-box"
                          role="link"
                          tabIndex={0}
                          onClick={() => router.push(routeHref)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              router.push(routeHref);
                            }
                          }}
                        >
                          <div className="stab-box2">
                            <div className="calender d-flex">
                              <p>{formatDate(item.date)}</p>
                            </div>
                            <p>
                              <span>{item.name}</span>
                            </p>
                          </div>
                          <div className="d-flex justify-content-end align-items-center gap-2">
                            <div>
                              {item?.downloadfile ? (
                                <a
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href={`${IMG_URL}${item.downloadfile}`}
                                  download=""
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <img
                                    className="visibility-eye"
                                    src="/icons/visibility.svg"
                                    alt="PDF icon"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </a>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </div>
            </Row>
          </Tab.Container>
        </div>

        <div className="col-lg-4 col-md-12 col-12 mt-5 mt-sm-3">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="head-h">{language == "en" ? "E-Portal" : "ഇ പോർട്ടൽ"}</h2>
          </div>
          <div className="row mt-2">
            {eportal?.map((item) => {
              const imageSrc = theme === "dark" ? item.darkphoto : item.lightphoto;
              const isValidUrl = item.url && item.url.startsWith("https");

              return (
                <div className="col-lg-6 col-md-6 col-6" key={item.id}>
                  {isValidUrl ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="service-box mt-3"
                    >
                      <img
                        src={`${IMG_URL}${imageSrc}`}
                        alt={item.name}
                        loading="lazy"
                        decoding="async"
                      />
                      <p>{item.name}</p>
                    </a>
                  ) : (
                    <div className="service-box mt-3" style={{ cursor: "default" }}>
                      <img
                        src={`${IMG_URL}${imageSrc}`}
                        alt={item.name}
                        loading="lazy"
                        decoding="async"
                      />
                      <p>{item.name}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
